import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { Button, Icon, NewTable, useToast } from '@vallourec-ui/vallourec-ui'
import { isAxiosError } from 'axios'

import { useTranslation } from 'react-i18next'

import { usePageTitle } from '../../hooks/usePageTitle'
import { Container } from './style'

import { TableWrapper } from '../Flow/_component/DataToResult/style'
import EditModal from './_components/EditModal'
import { PageProps } from '@/@types/global'
import { configReport } from '@/api/post'
import { useApp } from '@/stores/AppStore'

type FooterConfig = {
  id: string
  testName: string
  laboratoryName: string
  docIDBarreiro: string
  addressBarreiro: string
  generalTestDetail: string
  docIDJeceaba: string
  addressJeceaba: string
}

const Footer: React.FC<PageProps> = ({ title, subtitle }) => {
  usePageTitle({ title, subtitle })
  const { setIsLoading } = useApp()
  const { toast } = useToast()

  const { t } = useTranslation()

  const [footerConfiguration, setFooterConfiguration] = useState<
    FooterConfig[]
  >([])

  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false)
  const [selectedItem, setSelectedItem] = useState<any>(undefined)

  const columns = useMemo(
    () => [
      {
        id: 'button',
        accessorKey: 'button',
        header: '',
        // eslint-disable-next-line react/no-unstable-nested-components, @typescript-eslint/no-explicit-any
        cell: (row: any) => (
          <Button
            onClick={() => {
              setSelectedItem(row.row.original)
              setModalIsOpen(true)
            }}
            key="confirm-button"
            variant="secondary-grey"
          >
            <Icon name="EditLine" />
          </Button>
        ),
      },
      {
        id: 'displayId',
        accessorKey: 'displayId',
        header: t<string>('footerConfig.table.id', 'Id'),
      },
      {
        id: 'testName',
        accessorKey: 'testName',
        header: t<string>('footerConfig.table.testName'),
      },
      {
        id: 'laboratoryName',
        accessorKey: 'laboratoryName',
        header: t<string>('footerConfig.table.laboratoryName'),
      },
      {
        id: 'idDocB',
        accessorKey: 'idDocB',
        header: t<string>('footerConfig.table.docIDBarreiro'),
      },
      // {
      //   id: 'addressB',
      //   accessorKey: 'addressB',
      //   header: t<string>('footerConfig.table.addressBarreiro'),
      // },
      // {
      //   id: 'generalTestDetailB',
      //   accessorKey: 'generalTestDetailB',
      //   header: t<string>('footerConfig.table.generalTestDetailsBarreiro'),
      //   cell: (row: any) => (
      //     <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
      //       {row.getValue('generalTestDetailB')}
      //     </pre>
      //   ),
      // },
      {
        id: 'idDocJ',
        accessorKey: 'idDocJ',
        header: t<string>('footerConfig.table.docIDJeceaba'),
      },
      // {
      //   id: 'addressJ',
      //   accessorKey: 'addressJ',
      //   header: t<string>('footerConfig.table.addressJeceaba'),
      // },
      // {
      //   id: 'generalTestDetailJ',
      //   accessorKey: 'generalTestDetailJ',
      //   header: t<string>('footerConfig.table.generalTestDetailsJeceaba'),
      //   cell: (row: any) => (
      //     <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
      //       {row.getValue('generalTestDetailJ')}
      //     </pre>
      //   ),
      // },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, footerConfiguration]
  )

  const fetchData = async () => {
    try {
      setIsLoading(true)
      setFooterConfiguration([])
      const response = await configReport({})

      const mappedData: FooterConfig[] = (response?.items || []).map(
        (item: any) => ({
          ...item.content,
          displayId: t(`report.id.${item.id}`).toUpperCase(),
          id: item?.id,
          testName: item.content?.testName || '',
          laboratoryName: item.content?.laboratoryName || '',
          idDocB: item.content?.idDocB || '',
          addressB: item.content?.addressB || '',
          generalTestDetailB: item.content?.generalTestDetailB || '',
          idDocJ: item.content?.idDocJ || '',
          addressJ: item.content?.addressJ || '',
          generalTestDetailJ: item.content?.generalTestDetailJ || '',
        })
      )

      setFooterConfiguration(mappedData)
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err)
      if (isAxiosError(err)) {
        toast({
          title: 'Error',
          description: err?.message ? err?.message : t<string>('home.error'),
          type: 'error',
        })
      }
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t])

  const handleOnSave = async (payload) => {
    setModalIsOpen(false)
    setIsLoading(true)
    try {
      await configReport({ save: { content: payload, id: payload.id } })
      toast({
        title: t('success'),
        description: t('successDescription'),
        type: 'success',
      })
      await fetchData()
    } catch (err) {
      if (isAxiosError(err)) {
        toast({
          title: t('error'),
          description: err?.message
            ? err?.message
            : t<string>('errorDescription'),
          type: 'error',
        })
      }
    } finally {
      setIsLoading(false)
    }
  }

  const renderTable = useCallback(() => {
    return footerConfiguration?.length > 0 ? (
      <TableWrapper>
        <NewTable.Root
          key="table-with-data"
          columns={columns}
          data={footerConfiguration}
          hasPagination
          isBordered
        />
      </TableWrapper>
    ) : (
      <NewTable.Root key="table-without-data" data={[]} columns={columns} />
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [footerConfiguration, t])

  return (
    <Container>
      {renderTable()}
      <EditModal
        isOpen={modalIsOpen}
        data={selectedItem ?? ({} as any)}
        title={selectedItem?.displayId}
        setIsOpen={setModalIsOpen}
        onOutsideClick={() => {
          setModalIsOpen(false)
          setSelectedItem(undefined)
        }}
        onConfirm={handleOnSave}
      />
    </Container>
  )
}

export default Footer
